import React, { useState } from 'react'
import { Card, Button } from 'react-bootstrap'
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { useAuth, useProtectedPage } from '../../../context/AuthContext'
import useCallApi from '../../../hooks/useCallApi'
import callAPI from '../../../helpers/callAPI'
import Loading from '../../../templates/UI/Loading/Loading'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import Campaign from '../../../templates/Campaign/Campaign'
import { Link } from 'react-router-dom'
import Dollars from '../../../templates/Dollars/Dollars'

const AffiliateWeeklyPayments = ({ affiliateId, campaignId, showSystemMessage }) => {
    const auth = useAuth()
    useProtectedPage(auth.permissions, 'affiliate_weekly_payments')

    const { dataLoaded, data, refetch, setData } = useCallApi('affiliates/weeklyPayments', 'GET', {}, [])
    const [confirmScriptType, setConfirmScriptType] = useState(null)
    const [password, setPassword] = useState('')
    const [isProcessing, setIsProcessing] = useState(false)

    document.title = `Affiliate Weekly Payments`

    const toggleStatus = async (paymentId, currentStatus) => {
        const newStatus = currentStatus === 'P' ? 'X' : 'P'
        try {
            const response = await callAPI(`affiliates/weeklyPayments/?id=${paymentId}&status=${newStatus}`, 'PUT', {})

            if (response.success) {
                setData({
                    ...data,
                    payments: data.payments.map(payment =>
                        payment.id === paymentId
                            ? { ...payment, status: newStatus }
                            : payment
                    )
                })

                showSystemMessage('success', `Status updated to ${newStatus === 'P' ? 'Pending' : 'Will Not Process'}`)
            } else {
                showSystemMessage('error', 'Failed to update status')
            }
        } catch (error) {
            showSystemMessage('error', 'Failed to update status')
        }
    }

    const getReportOutput = () => {
        let columns = [
            {
                Header: 'Affiliate ID',
                accessor: 'affiliate_id',
                Cell: ({ value }) => <div>{value}</div>,
            },
            {
                Header: 'Affiliate',
                accessor: 'name',
                Cell: ({ row }) => <Affiliate aid={row.original.affiliate_id} name={row.original.name} />,
            },
            {
                Header: () => <div className="float-right">Amount</div>,
                className: "text-right",
                accessor: 'amount',
                Cell: ({ value }) => <Dollars value={value} />,
            },
            {
                Header: 'Transaction Description',
                accessor: 'transaction_description',
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ value }) => {
                    let statusText = ''

                    switch (value) {
                        case 'P':
                            statusText = 'Process'
                            break
                        case 'X':
                            statusText = 'Skip'
                            break
                        case 'D':
                            statusText = 'Duplicate'
                            break
                        default:
                            statusText = value
                    }

                    return <span className={value === 'P' ? 'text-success' : value === 'X' ? 'text-danger' : value === 'D' ? 'text-info' : ''}>{statusText}</span>
                },
            },
            {
                Header: 'Actions',
                accessor: 'id',
                Cell: ({ row }) => {
                    const { status, id } = row.original
                    let buttonText = ''
                    let buttonColor = ''

                    switch (status) {
                        case 'P':
                            buttonText = 'Skip'
                            buttonColor = 'danger'
                            break
                        case 'X':
                            buttonText = 'Process'
                            buttonColor = 'success'
                            break
                        case 'D':
                            buttonText = 'Process'
                            buttonColor = 'warning'
                            break
                        default:
                            return null
                    }

                    return (
                        <Button
                            className="btn btn-sm text-nowrap"
                            variant={buttonColor}
                            onClick={() => toggleStatus(id, status)}
                        >
                            {buttonText}
                        </Button>
                    )
                },
            }
        ]

        const filteredPayments = data.payments.filter(payment => payment.status !== 'A')

        return <ReportTable data={filteredPayments} columns={columns} />
    }

    const markAllAsPending = async () => {
        //uses same endpoint as toggleStatus but includes a query item 'batch = true' to mark all as pending
        try {
            const response = await callAPI(`affiliates/weeklyPayments/?batch=true&status=P`, 'PUT', {})

            if (response.success) {
                refetch()
                showSystemMessage('success', `All payments marked as processable.`)
            } else {
                showSystemMessage('error', 'Failed to mark all as processable')
            }
        } catch (error) {
            showSystemMessage('error', 'Failed to mark all as processable')
        }
    }

    const runScript = async (includeDuplicatePayments = false) => {
        setIsProcessing(true)
        try {
            const response = await callAPI(`affiliates/weeklyPayments/run`, 'POST', { payments: data.payments, password, includeDuplicatePayments })

            if (response.success) {
                setConfirmScriptType(null)
                refetch()
                showSystemMessage('success', `Weekly payments script executed successfully`)
            } else {
                showSystemMessage('error', response.message || 'Failed to execute weekly payments script')
            }
        } catch (error) {
            showSystemMessage('error', 'Failed to execute weekly payments script')
        } finally {
            setIsProcessing(false)
        }
    }

    const deleteDuplicates = async () => {
      //uses api endpoint to delete duplicates
      try {
        const response = await callAPI(`affiliates/weeklyPayments/deleteDuplicates`, 'DELETE', { password })
        if (response.success) {
          showSystemMessage('success', 'Duplicates deleted successfully')
          refetch()
        } else {
          showSystemMessage('error', response.message || 'Failed to delete duplicates')
        }
      } catch (error) {
        showSystemMessage('error', 'Failed to delete duplicates')
      } finally {
        setIsProcessing(false)
      }
    }

    const hasDuplicatePayments = data.success && data.payments.some(payment => payment.status === 'D')

    return (
        <div>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Affiliate Weekly Payments</h1>
            </div>
            <div className="row">
                <div className="col-lg-12">
                    <Card>
                        <Card.Header>
                            <input
                                type="password"
                                className="form-control form-control-sm ml-2"
                                style={{ width: '160px', display: 'inline-block' }}
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            {confirmScriptType === 'standard' ? (
                                <button
                                    className="btn btn-sm btn-info ml-2"
                                    disabled={isProcessing}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        runScript(false)
                                    }}
                                >
                                    {isProcessing ? 'Processing...' : 'Confirm'}
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm btn-info ml-2"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setConfirmScriptType('standard')
                                    }}
                                >
                                    Process Weekly Payments
                                </button>
                            )}

                            {hasDuplicatePayments && (
                                confirmScriptType === 'duplicates' ? (
                                    <button
                                        className="btn btn-sm btn-warning ml-2"
                                        disabled={isProcessing}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            runScript(true)
                                        }}
                                    >
                                        {isProcessing ? 'Processing...' : 'Confirm Duplicates'}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className="btn btn-sm btn-warning ml-2"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setConfirmScriptType('duplicates')
                                            }}
                                        >
                                            Process With Duplicates
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger ml-2"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                deleteDuplicates()
                                            }}
                                        >
                                            Delete Duplicates
                                        </button>
                                    </>
                                )
                            )}

                            {// if not all payments are processable, show the button to mark all as processable
                                data.success && (
                                    data.payments.some(payment => payment.status !== 'P') && (
                                        <button
                                            className="btn btn-sm btn-primary float-right"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                markAllAsPending();
                                            }}
                                        >
                                            Mark All as Processable
                                        </button>
                                    )
                                )}
                                
                        </Card.Header>
                        <Card.Body className="p-0">
                            {dataLoaded ? data.payments.length === 0 ? <div className="p-3">No stats for the current parameters.</div> : getReportOutput() : <Loading></Loading>}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    )
}

export default AffiliateWeeklyPayments
